import { isAfter, parseISO } from "date-fns"
import pluralize from "pluralize"
import { useEffect, useState } from "react"
import { ShareAttachmentFragment } from "~/__generated__/graphql"
import { AppText } from "~/ui/app-text"
import { PredictionsCountdown } from "./predictions-countdown"

type PredictionsData = NonNullable<ShareAttachmentFragment["predictions"]>

interface BannerItem {
  text: string
  backgroundColor: string
  textClassName?: string
}

const PREDICTIONS_BANNER: BannerItem[] = [
  { text: "PREDICTIONS", backgroundColor: "#FF6666" },
  { text: "PREDICTIONS", backgroundColor: "#FACEFF" },
  { text: "PREDICTIONS", backgroundColor: "#F6BE16" },
]

const RESULTS_BANNER: BannerItem[] = [
  { text: "PREDICTION RESULTS", backgroundColor: "#F5F5F5", textClassName: "text-support-green" },
  { text: "PREDICTION RESULTS", backgroundColor: "#25B963", textClassName: "text-white" },
]

// Full-bleed scrolling band mirroring the mobile marquee. -mx-4 cancels the card's p-4 so it spans
// edge to edge; the track is two identical halves and translates -50% for a seamless loop.
const PredictionsBanner: React.FC<{ items: BannerItem[] }> = ({ items }) => {
  // Repeat the items so one half is wide enough to fill the card with no gap mid-scroll.
  const half = [0, 1].flatMap((rep) => items.map((item, i) => ({ item, key: `${rep}-${i}` })))

  return (
    <div className="-mx-4 overflow-hidden">
      <div className="flex w-max animate-marquee motion-reduce:animate-none">
        {[0, 1].map((copy) => (
          <div key={copy} className="flex shrink-0" aria-hidden={copy === 1}>
            {half.map(({ item, key }) => (
              <div
                key={key}
                className="px-4 py-2"
                style={{ backgroundColor: item.backgroundColor }}
              >
                <AppText variant="h2" className={`whitespace-nowrap ${item.textClassName ?? ""}`}>
                  {item.text}
                </AppText>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

// Flip to the CLOSED layout the moment the game closes, even if the page stays open past endsAt
// (the render-time check alone never re-runs while mounted).
const useGameEnded = (endsAt: string) => {
  const endsAtMs = parseISO(endsAt).getTime()
  const [ended, setEnded] = useState(() => Date.now() >= endsAtMs)

  useEffect(() => {
    if (ended) return
    const ms = endsAtMs - Date.now()
    // setTimeout overflows past ~24.8 days; a close that far off needs no live flip.
    if (ms > 0x7fffffff) return
    const id = setTimeout(() => setEnded(true), Math.max(ms, 0))
    return () => clearTimeout(id)
  }, [endsAtMs, ended])

  return ended
}

interface Props {
  predictions: PredictionsData
  messageCreatedAt?: string
}

export const MessageAttachmentPredictions: React.FC<Props> = ({
  predictions,
  messageCreatedAt,
}) => {
  // A results message post-dates the game's close; mirrors isViewingPredictionResults in the app.
  const viewingResults =
    !!messageCreatedAt && isAfter(parseISO(messageCreatedAt), parseISO(predictions.endsAt))
  const gameEnded = useGameEnded(predictions.endsAt)
  const players = pluralize("Player", predictions.responseCount, true)

  return (
    <div className="flex w-full min-w-[260px] max-w-[360px] flex-col gap-4 overflow-hidden rounded-2xl border border-neutral-200 bg-white p-4">
      <div className="flex flex-col gap-4">
        <AppText variant="h3" className="text-center">
          {predictions.title}
        </AppText>
        <PredictionsBanner items={viewingResults ? RESULTS_BANNER : PREDICTIONS_BANNER} />
      </div>

      <div className="flex flex-col items-center gap-2 py-2">
        {viewingResults ? (
          <AppText variant="h3">{players}</AppText>
        ) : gameEnded ? (
          <AppText variant="h2" className="text-support-red">
            CLOSED
          </AppText>
        ) : (
          <>
            <PredictionsCountdown endsAt={predictions.endsAt} />
            <AppText variant="h3">{players}</AppText>
          </>
        )}
      </div>
    </div>
  )
}
